import { KustomerCustomerAttributes } from 'types/kustomer';

/**
 * Wrapper reutilizable del Kustomer Chat Web SDK.
 *
 * El script (widget.js) se carga en public/index.html y expone el global
 * `Kustomer` disparando el evento `kustomerLoaded` cuando termina de cargar.
 * Regla dura del SDK: TODO método (login, describeCustomer, ...) se invoca
 * DENTRO del callback de `Kustomer.start()`, nunca antes. Aquí se garantiza con
 * el flag `isReady`, que sólo se setea dentro de ese callback.
 *
 * Modelo de datos:
 * - Identidad → `Kustomer.login({ jwtToken })` con un JWT firmado por el backend.
 * - Empresa / corporateId → custom attributes del CUSTOMER, seteados vía
 *   `describeCustomer` tras el login (el JWT no transporta esos atributos).
 *
 * Diseñado como funciones puras a nivel de módulo (mismo patrón que
 * utils/clevertap.ts) para poder reutilizarse/extenderse desde otros módulos.
 */

// Tiempo máximo de espera del evento `kustomerLoaded` antes de marcar error.
const LOAD_TIMEOUT_MS = 5000;

// Llaves de custom attributes en el objeto CUSTOMER de Kustomer.
// IMPORTANTE: deben coincidir EXACTAMENTE con la "key" del klass en el objeto
// Customer (cópiala del ícono de portapapeles; NO es el display name). Ajustar
// aquí si Soporte las nombró distinto.
const CUST_ATTR_CORPORATE_NAME = 'corporateNameStr';
const CUST_ATTR_CORPORATE_ID = 'corporateIdStr';
const CUST_ATTR_USER_EMAIL = 'userEmailStr';
const CUST_ATTR_USER_UNIQUE_ID = 'userUniqueIdStr';

let isReady = false;
let hasError = false;
let isStarting = false;
// True desde que se invoca `Kustomer.start()`; permite un `stop()` seguro y
// re-arrancar (start) tras un logout.
let hasStarted = false;
// True cuando el flujo de login se completó (se obtuvo token y se invocó el
// login) en esta sesión; evita re-loguear ante cambios de `datalogin`. Se limpia
// en stopKustomer.
let isLoggedIn = false;
// True mientras hay un intento de login en curso (evita intentos concurrentes).
let isLoggingIn = false;

function getBrandId(): string | undefined {
  return process.env.REACT_APP_KUSTOMER_BRAND_ID?.trim() || undefined;
}

function markError(onError?: (error?: unknown) => void, error?: unknown): void {
  if (hasError) {
    return;
  }
  hasError = true;
  isStarting = false;
  // eslint-disable-next-line no-console
  console.warn('[Kustomer] SDK failed to initialize.', error ?? '');
  onError?.(error);
}

/**
 * Inicializa el widget de Kustomer de forma idempotente.
 * @param onReady Se invoca dentro del callback de Kustomer.start().
 * @param onError Se invoca si el SDK no carga (timeout) o falla al iniciar.
 */
export function startKustomer(
  onReady?: () => void,
  onError?: (error?: unknown) => void,
): void {
  if (isReady) {
    onReady?.();
    return;
  }
  if (isStarting) {
    return;
  }
  isStarting = true;
  hasError = false;

  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const start = (): void => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    // Ya no dependemos del evento: quitar el listener por si `start` se invocó
    // por el camino inmediato (evita que quede colgado o dispare de nuevo).
    window.removeEventListener('kustomerLoaded', start);
    // Guarda defensiva contra doble arranque (ej. dos handlers encolados).
    if (hasStarted || isReady) {
      return;
    }
    if (!globalThis.Kustomer) {
      markError(onError, new Error('Global Kustomer not available.'));
      return;
    }
    const brandId = getBrandId();
    if (!brandId) {
      markError(
        onError,
        new Error('REACT_APP_KUSTOMER_BRAND_ID is not defined.'),
      );
      return;
    }
    try {
      hasStarted = true;
      globalThis.Kustomer.start({ brandId }, () => {
        // Sólo aquí el SDK está listo para recibir login/describeCustomer.
        isReady = true;
        isStarting = false;
        hasError = false;
        onReady?.();
      });
    } catch (error) {
      markError(onError, error);
    }
  };

  timeoutId = setTimeout(() => {
    // Remueve el listener colgado: si no, al reintentar quedarían dos y ambos
    // dispararían start() cuando el SDK cargue tarde (Kustomer.start duplicado).
    window.removeEventListener('kustomerLoaded', start);
    markError(onError, new Error('Timeout waiting for `kustomerLoaded`.'));
  }, LOAD_TIMEOUT_MS);

  if (globalThis.Kustomer) {
    start();
    return;
  }

  // El script aún no cargó: esperar el evento una sola vez.
  window.addEventListener('kustomerLoaded', start, { once: true });
}

export function isKustomerReady(): boolean {
  return isReady;
}

export function hasKustomerError(): boolean {
  return hasError;
}

/**
 * Autentica al cliente en Kustomer con un JWT firmado por el backend.
 * @param fetchToken Función que obtiene un JWT FRESCO (el `iat` debe tener < 15
 *   min), típicamente pegándole al endpoint del backend. Se invoca justo antes
 *   del login para que el token nunca llegue vencido.
 *
 * Guards:
 * - `isLoggingIn`: evita intentos concurrentes.
 * - `isLoggedIn`: se marca SÓLO cuando se obtuvo un token y se invocó el login,
 *   para no re-loguear ante cada cambio de `datalogin`. Si `fetchToken` falla o
 *   devuelve vacío, NO se marca → el login es reintentable en un próximo trigger
 *   (ej. si la red se recupera). Se limpia en stopKustomer.
 *
 * Debe llamarse con el SDK ya listo (dentro/después del callback de start).
 * @param attributes Atributos corporativos a setear en el Customer tras el
 *   login (el JWT no los transporta; se envían con `describeCustomer`).
 */
export async function loginKustomer(
  fetchToken: () => Promise<string | undefined>,
  attributes?: KustomerCustomerAttributes,
): Promise<void> {
  if (!isReady || !globalThis.Kustomer || isLoggedIn || isLoggingIn) {
    return;
  }
  isLoggingIn = true;

  try {
    const jwtToken = await fetchToken();
    if (!jwtToken) {
      // Sin token (fallo o respuesta vacía): no se compromete el guard para
      // permitir reintentar más adelante.
      return;
    }

    // Token obtenido: se marca como logueado para no re-loguear. Si Kustomer
    // rechaza el token, es un tema de config/secret que reintentar no resuelve.
    isLoggedIn = true;
    globalThis.Kustomer.login({ jwtToken }, (_response, error) => {
      if (error) {
        // eslint-disable-next-line no-console
        console.warn('[Kustomer] login failed:', error);
        return;
      }
      // El customer ya está verificado: recién ahora `describeCustomer` acepta
      // los custom attributes del Customer (el JWT no los trae).
      if (attributes) {
        applyCustomerAttributes(attributes);
      }
    });
  } catch (error) {
    // fetchToken falló (red/backend): reintentable en el próximo trigger.
    // eslint-disable-next-line no-console
    console.warn('[Kustomer] login failed:', error);
  } finally {
    isLoggingIn = false;
  }
}

/**
 * Setea los atributos corporativos en el Customer ya autenticado (tras el
 * login). El JWT no transporta estos custom attributes, por eso se envían
 * explícitamente con `describeCustomer`. No-op si no hay atributos que enviar.
 */
function applyCustomerAttributes(attributes: KustomerCustomerAttributes): void {
  if (!globalThis.Kustomer) {
    return;
  }

  const customAttributes: Record<string, unknown> = {};
  if (attributes.corporateName) {
    customAttributes[CUST_ATTR_CORPORATE_NAME] = attributes.corporateName;
  }
  if (attributes.corporateId) {
    customAttributes[CUST_ATTR_CORPORATE_ID] = attributes.corporateId;
  }
  if (attributes.userEmail) {
    customAttributes[CUST_ATTR_USER_EMAIL] = attributes.userEmail;
  }
  if (attributes.userUniqueId) {
    customAttributes[CUST_ATTR_USER_UNIQUE_ID] = attributes.userUniqueId;
  }

  if (!Object.keys(customAttributes).length) {
    return;
  }

  // Los custom attributes van en su propia key `customAttributes`, NO bajo
  // `attributes` (que sólo acepta identificadores compartidos: sharedEmails...).
  globalThis.Kustomer.describeCustomer(
    { customAttributes },
    (_response, error) => {
      if (error) {
        // eslint-disable-next-line no-console
        console.warn('[Kustomer] describeCustomer failed:', error);
      }
    },
  );
}

/** Abre el widget de chat de forma programática (reutilizable a futuro). */
export function openKustomer(): void {
  globalThis.Kustomer?.open();
}

/**
 * Detiene el widget: cierra la sesión autenticada (`logout`) y lo quita de la
 * página (`stop`), ocultando el bubble. Se llama cuando no hay sesión del panel
 * para que no se pueda chatear sin estar logueado. Idempotente y seguro aunque
 * el SDK no se haya arrancado. Deja el estado listo para un nuevo `start`.
 */
export function stopKustomer(): void {
  if (!globalThis.Kustomer || !hasStarted) {
    return;
  }

  try {
    globalThis.Kustomer.logout();
    globalThis.Kustomer.stop();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[Kustomer] stop failed:', error);
  }

  hasStarted = false;
  isReady = false;
  isStarting = false;
  hasError = false;
  isLoggedIn = false;
  isLoggingIn = false;
}
